import React, { useMemo, useState } from "react"
import { Button, Select, Input, Flex, Typography, Divider, Form, Spin } from "antd"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { cancelGuideBookingsBulk, fetchRefundableAmount } from "store/bookings"
import { toaster } from "components/common/Toast"
import { useTranslation } from "react-i18next"
import styles from "./index.module.css"
import { LoadingOutlined } from "@ant-design/icons"
import { Modal } from "ui/Modal"
import { getCalendarBookings } from "store/calendar"
import useSearchFilter from "hooks/useSearchFilter"
import { useCalendarDateRange } from "pagesV2/pro/Calendar/useCalendarDateRange"
import { useLocation, useParams } from "react-router-dom"
import { numberToCurrency } from "helpers/string"
import { REFUND_CANCEL_REASONS } from "../constants"
const { Text } = Typography

const CancelBookingModal = ({ open, onCancel, onConfirm, bookings, reasons }) => {
  const location = useLocation()
  const [isRefundable, setIsRefundable] = useState(false)
  const path = location?.pathname
  const { id } = useParams()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [params] = useSearchFilter({
    accessAttributes: ["range", "date", "weather", "trip_id", "journal_date", "unavailable_id"]
  })
  const dates = useCalendarDateRange(params)

  const handleSelectedReason = (_, selectedReason) => {
    const uuid = bookings?.[0]?.uuid || id
    const isRefundableSelected = REFUND_CANCEL_REASONS.includes(selectedReason?.value)
    setIsRefundable(isRefundableSelected)
    if (isRefundableSelected && uuid && selectedReason?.id) {
      dispatch(fetchRefundableAmount(uuid, selectedReason.id))
    }
  }

  const { data, loading } = useSelector((state) => state.bookings.refundableAmount || {})
  const refundAmount = data?.refundable_amount || 0
  const handleConfirm = async () => {
    try {
      setIsLoading(true)
      const values = await form.validateFields()
      const payload = bookings?.map((booking, idx) => ({
        id: booking?.id,
        reason: values?.reasons[idx]?.reasonType === "Other" ? values?.reasons[idx]?.reasonText : values?.reasons[idx]?.reasonType
      }))
      const result = await dispatch(cancelGuideBookingsBulk(payload))
      if (result?.message) toaster.success(result.message)

      const payloadParam = {
        "q[date_gteq]": dates?.start,
        "q[date_lteq]": dates?.end
      }
      if (path?.includes("calendar")) {
        dispatch(getCalendarBookings(payloadParam))
      }

      if (onConfirm) onConfirm()
    } catch (e) {
      console.error("Cancel booking error:", e)
    } finally {
      setIsLoading(false)
    }
  }

  const reasonOptions = useMemo(
    () =>
      reasons?.map((cancelReason) => {
        const { id, reason } = cancelReason || {}
        return { id, label: reason, value: reason }
      }),
    [reasons]
  )

  return (
    <Modal open={open} onCancel={onCancel} footer={null} width={700} title={`Cancel Booking?`} destroyOnClose centered>
      <div className={styles.cancelBookingModalContent}>
        <Text style={{ fontWeight: 500, fontSize: 16 }}>Are you sure you want to cancel booking{bookings?.length > 1 ? "s" : ""}?</Text>
        <Divider />
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            reasons: bookings?.map(() => ({ reasonType: undefined, reasonText: "" }))
          }}
        >
          <Form.List name="reasons">
            {(fields) => (
              <>
                {fields?.map((field, idx) => (
                  <>
                    <div key={bookings?.[idx]?.id} style={{ marginBottom: 32, background: "#f7f9fa", borderRadius: 12, padding: 16 }}>
                      <Flex align="center" gap={16} style={{ marginBottom: 12 }}>
                        <img
                          src={bookings?.[idx]?.image}
                          alt="trip"
                          style={{ width: 60, height: 60, borderRadius: 8, objectFit: "cover" }}
                        />
                        <div>
                          <Text strong>{bookings?.[idx]?.title}</Text>
                          <div style={{ fontSize: 13, color: "#100d0d" }}>ID {bookings?.[idx]?.booking_id}</div>
                          <div style={{ fontSize: 13, color: "#100d0d" }}>Booked on {bookings?.[idx]?.booked_on}</div>
                        </div>
                      </Flex>
                    </div>
                    <Form.Item
                      name={[field.name, "reasonType"]}
                      label={<Text strong>Cancellation Reason*</Text>}
                      rules={[{ required: true, message: "Please select a reason." }]}
                      required={false}
                    >
                      <Select
                        style={{ width: "100%", marginTop: 4 }}
                        placeholder="Select Reason"
                        options={reasonOptions}
                        onChange={handleSelectedReason}
                      />
                    </Form.Item>
                    {isRefundable && (
                      <div className="mb-4">
                        {loading ? (
                          <Flex justify="center" align="center" gap={8}>
                            <Spin size="small" style={{ marginLeft: 8 }} />
                          </Flex>
                        ) : (
                          <Flex>
                            <Text type="primary" className={styles.refundAmountText}>
                              Refund Amount:
                            </Text>
                            <Text type="primary" style={{ marginLeft: 8 }}>
                              {numberToCurrency(refundAmount)}
                            </Text>
                          </Flex>
                        )}
                      </div>
                    )}

                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.reasons?.[idx]?.reasonType !== curr.reasons?.[idx]?.reasonType}>
                      {({ getFieldValue }) =>
                        getFieldValue(["reasons", idx, "reasonType"]) === "Other" && (
                          <Form.Item
                            name={[field.name, "reasonText"]}
                            label="Reason*"
                            rules={[{ required: true, message: "Please provide a custom reason." }]}
                            required={false}
                          >
                            <Input.TextArea placeholder="Add Reason" rows={3} />
                          </Form.Item>
                        )
                      }
                    </Form.Item>
                  </>
                ))}
              </>
            )}
          </Form.List>
        </Form>
      </div>
      <Flex gap={12}>
        <Button variant="outlined" color="primary" onClick={onCancel}>
          {t("global.no")}
        </Button>
        <Button color="danger" key={form} variant="solid" onClick={handleConfirm}>
          {isRefundable ? t("global.refund_and_cancel") : t("global.yes")}
        </Button>
      </Flex>
      {isLoading && (
        <Flex key={isLoading} justify="center" align="center" className={`position-absolute ${styles.modalLoaderWrapper}`}>
          <Spin className="sa" indicator={<LoadingOutlined spin />} size="large" />
        </Flex>
      )}
    </Modal>
  )
}

CancelBookingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  bookings: PropTypes.array.isRequired,
  reasons: PropTypes.array.isRequired
}

export default CancelBookingModal
